import React, { useRef } from "react";
import DPicker from "react-multi-date-picker";
import { useTranslation } from "react-i18next";
import gregorian_en from "react-date-object/locales/gregorian_en";
import "../DatePicker/style.scss";

import { weekStartsSundayFor } from "../../../utils/analytics-period";
import { getCurrentContryCode } from "../../../services/restaurant";
import classNames from "classnames";
import { isRTL } from "../../../services/userModel";

// The period label doubles as a calendar trigger, using the SAME react-multi-date-picker and
// customDateTimePicker styling as the shifts screen (minus the time column):
//   day   → plain date panel;
//   week  → rmdp weekPicker (a click selects the whole week);
//   month → onlyMonthPicker (year/month panel, no day grid).
// The picked date becomes the page ANCHOR; analytics-period derives the account-correct bounds
// (its own week start), so the panel highlight is just a preview. Selection applies on click —
// no save/close buttons here.
const ApPeriodPicker = ({ granularity, anchor, label, onPick }) => {
  const ref = useRef(null);
  const { t } = useTranslation();

  let weekStartsSunday = true;
  try { weekStartsSunday = weekStartsSundayFor(getCurrentContryCode()); } catch (e) { /* store not ready */ }

  // Same app-i18n month/weekday names as the shared DatePicker component.
  const locale = {
    name: "custom_locale",
    digits: gregorian_en.digits,
    meridiems: gregorian_en.meridiems,
    weekDays: (["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"])
      .map(d => [t("datetimepicker.days." + d), t("datetimepicker.days." + d)]),
    months: ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", "JULY",
      "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"]
      .map(m => [t("datetimepicker.month." + m)])
  };

  const today = new Date();

  return (
    <DPicker
      ref={ref}
      portal
      zIndex={1100}
      calendarPosition="auto"
      className={classNames("customDateTimePicker ap-period-cal", {rtl: isRTL()})}
      value={anchor}
      onlyMonthPicker={granularity === "month"}
      weekPicker={granularity === "week"}
      weekStartDayIndex={weekStartsSunday ? 0 : 1}
      locale={locale}
      weekDays={locale.weekDays}
      maxDate={today}
      onChange={d => {
        const picked = Array.isArray(d) ? d[0] : d;
        if (!picked) return;
        const js = picked.toDate ? picked.toDate() : new Date(picked);
        // analytics has no future: clamp (a week/month containing today may carry future edges)
        onPick(js > today ? today : js);
        if (ref.current && ref.current.closeCalendar) ref.current.closeCalendar();
      }}
      render={(value, openCalendar) => (
        <button type="button" className="ap-period" onClick={openCalendar}>{label}</button>
      )}
    />
  );
};

export default ApPeriodPicker;
